/**
 * TEAM-ONLY GUARD
 * ================
 *
 * Restreint l'accès aux environnements dev/acceptance uniquement aux membres de l'équipe.
 *
 * Fonctionnement:
 * - Vérifie si l'utilisateur est authentifié avec Firebase Auth
 * - Vérifie l'appartenance équipe CÔTÉ SERVEUR via la CF `checkTeamAccess`
 *   (#338 — la whitelist n'est plus embarquée dans le bundle ; raccourci
 *   local uniquement pour le domaine @befret.be, non sensible)
 * - Redirige vers /team-login si non authentifié
 * - Redirige vers /unauthorized si authentifié mais non autorisé
 * - Désactivé automatiquement en production
 *
 * Configuration:
 * - Actif uniquement si environment.requireTeamAuth = true
 * - Whitelist serveur : param TEAM_ACCESS_EMAILS (functions/.env.befret-<env>)
 */

import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../environment';
import { TeamAccessService } from '../services/team-access.service';

@Injectable({
  providedIn: 'root'
})
export class TeamOnlyGuard implements CanActivate {

  constructor(
    private auth: Auth,
    private router: Router,
    private teamAccess: TeamAccessService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {

    // ✅ DÉSACTIVÉ EN PRODUCTION
    if (environment.production && !environment.requireTeamAuth) {
      console.log('🔓 [TEAM-ONLY GUARD] Désactivé en production');
      return true;
    }

    // ✅ DÉSACTIVÉ SI PAS DE CONFIGURATION
    if (!environment.requireTeamAuth) {
      console.log('🔓 [TEAM-ONLY GUARD] Désactivé (requireTeamAuth = false)');
      return true;
    }

    // ⚠️ Attendre que Firebase Auth ait fini de restaurer la session depuis
    // IndexedDB avant de lire `currentUser`. Sans ça, le guard se déclenche
    // avant l'init et la première émission de `authState` peut être `null`
    // pour un user en réalité connecté (race observée après redirect Stripe
    // sur /vm/shipping-success et /thank-you).
    await this.auth.authStateReady();
    const user = this.auth.currentUser;

    if (!user) {
      console.warn('🚫 [TEAM-ONLY GUARD] Utilisateur non authentifié');
      return this.router.createUrlTree(['/team-login'], {
        queryParams: { returnUrl: state.url }
      });
    }

    const userEmail = user.email || '';

    // Raccourci local non sensible : le domaine @befret.be est toujours
    // autorisé (évite un aller-retour CF pour le staff).
    if (userEmail.endsWith('@befret.be')) {
      console.log(`✅ [TEAM-ONLY GUARD] Accès autorisé (domaine): ${userEmail}`);
      return true;
    }

    // 🎯 #338 : vérification serveur (CF checkTeamAccess) — le front ne
    // connaît plus la whitelist.
    if (await this.teamAccess.isTeamMember(user.uid)) {
      console.log(`✅ [TEAM-ONLY GUARD] Accès autorisé (whitelist serveur): ${userEmail}`);
      return true;
    }

    console.warn(`🚫 [TEAM-ONLY GUARD] Email non autorisé: ${userEmail}`);
    return this.router.createUrlTree(['/unauthorized']);
  }
}
